import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import MaterielProfileLayout from './MaterielProfileLayout';

// import utility file
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import { ExclamationTriangleFill } from 'react-bootstrap-icons';
import MaterielService from '../../services/MaterielService';
import LocalService from '../../services/LocalService';

const ViewMateriel = ({ onClick }) => {
	const { id } = useParams();
	const [materiel, setMateriel] = useState(null);
	const [validated, setValidated] = useState(false);
	const [feedbackMessages, setFeedbackMessages] = useState({});
	const [showAlert, setShowAlert] = useState(false);
	const [alertMessage, setAlertMessage] = useState('');
	const [selectedEtat, setSelectedEtat] = useState('');
	const [selectedLocal, setSelectedLocal] = useState('');
	const [localOptions, setLocalOptions] = useState([]);

	useEffect(() => {
		Promise.all([MaterielService.getMateriel(id)]).then(
			([materielData, eleveData]) => {
				// Added "eleveData" to unpack second promise result
				setMateriel(materielData.data);

				if (materielData && materielData.data) {
					const etatMateriel = materielData.data.etat;
					setSelectedEtat(etatMateriel);

					const localMateriel = materielData.data.local;
					setSelectedLocal(localMateriel);
				}
			}
		);
	}, [id]);

	useEffect(() => {
		const fetchLocalOptions = async () => {
			try {
				const local = await LocalService.getLocalOptions();
				setLocalOptions(local);
			} catch (error) {
				console.error('Error fetching local options', error);
			}
		};

		fetchLocalOptions();
	}, []);

	const LOCAL_CHOICES = localOptions;

	const ETAT_CHOICES = [
		{ value: 'neuf', label: 'Neuf' },
		{ value: 'bon', label: 'Bon' },
		{ value: 'usé', label: 'Usé' }
	];

	const handleSubmit = async (event) => {
		event.preventDefault();
		const form = event.currentTarget;

		if (form.checkValidity() === false) {
			event.stopPropagation();
		} else {
			try {
				const materielData = new FormData();

				// Add all your form data to the FormData
				materielData.append('nom', form.nom.value);
				materielData.append('description', form.description.value);
				materielData.append('fournisseur', form.fournisseur.value);
				materielData.append('date_achat', form.date_achat.value);
				materielData.append('quantite', form.quantite.value);
				materielData.append('cout', form.cout.value);
				materielData.append('etat', form.etat.value);
				materielData.append('local', form.local.value);

				// Call the EleveService to create the eleve
				const response = await MaterielService.updateMateriel(id, materielData);

				// Reset the form and state variables after successful update
				form.reset();
				setValidated(false);
				setFeedbackMessages({});

				// Add this line to refresh the page
				window.location.reload();
			} catch (error) {
				console.error(
					'Erreur lors de la mise à jour des informations du matériel',
					error
				);

				if (error.response && error.response.data) {
					const apiErrors = error.response.data;

					// If there are field-specific errors
					if (typeof apiErrors === 'object' && !Array.isArray(apiErrors)) {
						const newFeedbackMessages = {};

						for (const field in apiErrors) {
							newFeedbackMessages[field] = apiErrors[field][0];
						}

						setFeedbackMessages(newFeedbackMessages);
					}

					// If there's a general error detail message
					if (apiErrors.detail) {
						setAlertMessage(apiErrors.detail[0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					} else if (
						apiErrors['Il ya erreur'] &&
						apiErrors['Il ya erreur'][0]
					) {
						setAlertMessage(apiErrors['Il ya erreur'][0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					}
				} else {
					alert(
						"Une erreur s'est produite lors de la soumission du formulaire."
					);
				}
			}
		}

		setValidated(true);
	};

	return (
		<MaterielProfileLayout>
			{materiel && (
				<>
					<Card className="mb-3 ">
						{/* Card body */}
						<Card.Body>
							{/*  Form  */}
							<Form
								className="mb-3"
								noValidate
								validated={validated}
								onSubmit={handleSubmit}
							>
								{showAlert && (
									<Alert variant="danger">
										<ExclamationTriangleFill size={25} className="me-1" />
										{alertMessage}
									</Alert>
								)}

								<Row>
									<Col xs={12} className="mb-3">
										{/* nom  */}
										<Form.Group>
											<Form.Label htmlFor="nom">
												Nom <span className="text-danger">*</span>
											</Form.Label>
											<Form.Control
												name="nom"
												type="text"
												placeholder="Saisir le nom du Materiel"
												id="nom"
												required
												isInvalid={!!feedbackMessages['nom']}
												onChange={(e) => {
													setFeedbackMessages((prevMessages) => ({
														...prevMessages,
														nom: e.target.value ? '' : 'Le nom est requis.'
													}));
												}}
												defaultValue={materiel.nom}
											/>
											<Form.Control.Feedback type="invalid">
												{feedbackMessages['nom']}
											</Form.Control.Feedback>
										</Form.Group>
									</Col>
									{/*description  */}
									<Col xs={12} className="mb-3">
										<Form.Group>
											<Form.Label htmlFor="description">
												Description <span className="text-danger">*</span>
											</Form.Label>
											<Form.Control
												name="description"
												as="textarea"
												placeholder="Saisir la description du Materiel"
												id="description"
												required
												isInvalid={!!feedbackMessages['description']}
												onChange={(e) => {
													setFeedbackMessages((prevMessages) => ({
														...prevMessages,
														description: e.target.value
															? ''
															: 'La description est requis.'
													}));
												}}
												defaultValue={materiel.description}
											/>
											<Form.Control.Feedback type="invalid">
												{feedbackMessages['description']}
											</Form.Control.Feedback>
										</Form.Group>
									</Col>

									{/* date_achat */}
									<Col xs={12} className="mb-3">
										<Form.Group className="mb-3">
											<Form.Label htmlFor="date_achat">
												Date d'achat <span className="text-danger">*</span>
											</Form.Label>
											<Form.Control
												name="date_achat"
												type="text"
												placeholder="Saisir la Date d'achat du Materiel"
												id="date_achat"
												required
												isInvalid={!!feedbackMessages['date_achat']}
												onChange={(e) => {
													setFeedbackMessages((prevMessages) => ({
														...prevMessages,
														date_achat: e.target.value
															? ''
															: "la Date d'achat est requise."
													}));
												}}
												defaultValue={materiel.date_achat}
											/>
											<Form.Control.Feedback type="invalid">
												{feedbackMessages['date_achat']}
											</Form.Control.Feedback>
										</Form.Group>
									</Col>

									<Col xs={12} className="mb-3">
										{/*  cout  */}
										<Form.Group>
											<Form.Label htmlFor="cout">Cout</Form.Label>
											<Form.Control
												name="cout"
												type="cout"
												placeholder="nom@example.com"
												id="cout"
												isInvalid={!!feedbackMessages['cout']}
												onChange={(e) => {
													setFeedbackMessages((prevMessages) => ({
														...prevMessages,
														cout: e.target.value
															? ''
															: "Le cout n'est pas valide."
													}));
												}}
												defaultValue={materiel.cout}
											/>
											<Form.Control.Feedback type="invalid">
												{feedbackMessages['cout']}
											</Form.Control.Feedback>
										</Form.Group>
									</Col>

									{/* fournisseur */}
									<Col xs={12} className="mb-3">
										<Form.Group className="mb-3">
											<Form.Label htmlFor="fournisseur">
												Fournisseur <span className="text-danger">*</span>
											</Form.Label>
											<Form.Control
												name="fournisseur"
												type="text"
												placeholder="Saisir le fournisseur du Materiel"
												id="fournisseur"
												isInvalid={!!feedbackMessages['fournisseur']}
												onChange={(e) => {
													setFeedbackMessages((prevMessages) => ({
														...prevMessages,
														etat: e.target.value ? '' : ''
													}));
												}}
												defaultValue={materiel.fournisseur}
											/>
											<Form.Control.Feedback type="invalid">
												{feedbackMessages['fournisseur']}
											</Form.Control.Feedback>
										</Form.Group>
									</Col>

									{/* etat */}
									<Col xs={12} className="mb-3">
										<Form.Group className="mb-3">
											<Form.Label htmlFor="etat">
												Etat <span className="text-danger">*</span>
											</Form.Label>
											<Form.Select
												name="etat"
												required
												value={selectedEtat}
												isInvalid={!!feedbackMessages['etat']}
												onChange={(e) => {
													const value = e.target.value;
													setSelectedEtat(value);
													// Reset feedback messages for points fields when etat changes
													setFeedbackMessages({});
												}}
											>
												<option value="">Sélectionner l'état</option>
												{ETAT_CHOICES.map((choice, index) => (
													<option key={index} value={choice.value}>
														{choice.label}
													</option>
												))}
											</Form.Select>
											<Form.Control.Feedback type="invalid">
												{feedbackMessages['etat']}
											</Form.Control.Feedback>
										</Form.Group>
									</Col>

									<Col xs={12} className="mb-3">
										{/* Quantite */}
										<Form.Group>
											<Form.Label htmlFor="quantite">
												Quantite <span className="text-danger">*</span>
											</Form.Label>
											<Form.Control
												name="quantite"
												type="number"
												placeholder="Saisir la Quantite du Materiel"
												id="quantite"
												required
												isInvalid={!!feedbackMessages['quantite']}
												onChange={(e) => {
													setFeedbackMessages((prevMessages) => ({
														...prevMessages,
														quantite: e.target.value
															? ''
															: 'La Quantite est requis.'
													}));
												}}
												defaultValue={materiel.quantite}
											/>
											<Form.Control.Feedback type="invalid">
												{feedbackMessages['quantite']}
											</Form.Control.Feedback>
										</Form.Group>
									</Col>

									{/* local */}
									<Col xs={12} className="mb-3">
										<Form.Group className="mb-3">
											<Form.Label htmlFor="local">
												Local <span className="text-danger">*</span>
											</Form.Label>
											<Form.Select
												name="local"
												required
												value={selectedLocal}
												isInvalid={!!feedbackMessages['local']}
												onChange={(e) => {
													const value = e.target.value;
													setSelectedLocal(value);
													// Reset feedback messages for points fields when local changes
													setFeedbackMessages({});
												}}
											>
												<option value="">Sélectionner le local</option>
												{LOCAL_CHOICES.map((choice, index) => (
													<option key={index} value={choice.value}>
														{choice.label}
													</option>
												))}
											</Form.Select>
											<Form.Control.Feedback local="invalid">
												{feedbackMessages['local']}
											</Form.Control.Feedback>
										</Form.Group>
									</Col>

									{/* Buttons */}
									<Col xs={12} className="mt-4">
										<Button variant="success" type="submit">
											Mettre à jour
										</Button>
										<Button
											type="reset"
											variant="outline-danger"
											className="ms-2"
										>
											Effacer
										</Button>
									</Col>
								</Row>
							</Form>
						</Card.Body>
					</Card>
				</>
			)}
		</MaterielProfileLayout>
	);
};

export default ViewMateriel;
